import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE } from './apiBase';

/** Client axios con cookie httpOnly (auth JEVIS Kernel). */
export const apiClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshPromise: Promise<boolean> | null = null;

function isAuthRefreshUrl(url: string): boolean {
  return /(?:^|\/)auth\/refresh\/?(?:\?|$)/.test(url);
}

/** Una sola refresh in-flight condivisa tra axios e fetch. */
async function silentRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(`${API_BASE}auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        return res.ok;
      } catch {
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryConfig | undefined;
    if (!config || error.response?.status !== 401 || config._retry) {
      return Promise.reject(error);
    }
    const url = config.url || '';
    if (isAuthRefreshUrl(url)) {
      return Promise.reject(error);
    }
    config._retry = true;
    const refreshed = await silentRefresh();
    if (!refreshed) {
      return Promise.reject(error);
    }
    return apiClient.request(config);
  },
);

/** fetch same-origin con cookie di sessione e silent refresh su 401. */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const normalized = path.startsWith('http')
    ? path
    : `${API_BASE}${path.replace(/^\//, '')}`;

  const doFetch = () =>
    fetch(normalized, {
      ...init,
      credentials: 'include',
    });

  const response = await doFetch();
  if (response.status !== 401 || isAuthRefreshUrl(normalized)) {
    return response;
  }

  const refreshed = await silentRefresh();
  if (!refreshed) {
    return response;
  }
  return doFetch();
}
